import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks';
import { subscriptionService } from '../services';
import { PLANS } from '../config/pricing';
import { getPlanPrice } from '../utils/pricing';

const SubscriptionContext = createContext();

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

export const TRIAL_DAYS = 7;

const isSubscribedStatus = (status) => status === 'active' || status === 'trial';

const isPendingStatus = (status) => status === 'pending';

export const SubscriptionProvider = ({ children }) => {
    const { isAuthenticated, isInitialized } = useAuth();
    const [plan, setPlan] = useState(null);
    const [status, setStatus] = useState(null);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const getInitialPlan = () => {
        try {
            const stored = localStorage.getItem('subscriptionPlan');
            if (stored && Object.values(PLANS).includes(stored)) {
                return stored;
            }
        } catch (error) {
            console.error('Error reading subscription plan from localStorage:', error);
        }
        return null;
    };

    const applySubscriptionData = useCallback((data) => {
        if (data?.plan && isSubscribedStatus(data?.status)) {
            setPlan(data.plan);
            setStatus(data.status);
            setSubscriptionDetails(data);
            try {
                localStorage.setItem('subscriptionPlan', data.plan);
            } catch (error) {
                console.error('Error saving subscription plan to localStorage:', error);
            }
            return;
        }

        setPlan(null);
        setStatus(data?.status || null);
        setSubscriptionDetails(data || null);
        try {
            localStorage.removeItem('subscriptionPlan');
        } catch (error) {
            console.error('Error clearing subscription plan from localStorage:', error);
        }
    }, []);

    const fetchSubscription = useCallback(async () => {
        if (!isInitialized) {
            return;
        }

        if (!isAuthenticated) {
            setPlan(null);
            setStatus(null);
            setSubscriptionDetails(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await subscriptionService.getSubscription();
            applySubscriptionData(response?.data || null);
        } catch (error) {
            console.warn('Failed to fetch subscription from API:', error);
            const fallbackPlan = getInitialPlan();
            setPlan(fallbackPlan);
            setStatus(fallbackPlan ? 'active' : null);
        } finally {
            setIsLoading(false);
        }
    }, [applySubscriptionData, isAuthenticated, isInitialized]);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    const isSilver = () => plan === PLANS.SILVER;
    const isGold = () => plan === PLANS.GOLD;
    const isPlatinum = () => plan === PLANS.PLATINUM;

    const hasAnalytics = () => isGold() || isPlatinum();
    const hasWebSocket = () => isPlatinum();
    const hasQROrdering = () => isPlatinum();
    const hasMediaImages = () => isPlatinum();
    const hasExpenseReceipts = () => {
        if (subscriptionDetails?.hasExpenseReceipts !== undefined) {
            return Boolean(subscriptionDetails.hasExpenseReceipts);
        }
        return isPlatinum();
    };
    const hasCustomDishCategories = () => isGold() || isPlatinum();

    const getPrice = () => getPlanPrice(plan);

    const updatePlan = (newPlan) => {
        if (Object.values(PLANS).includes(newPlan)) {
            setPlan(newPlan);
            setStatus('active');
        } else {
            console.error('Invalid plan:', newPlan);
        }
    };

    const hasActiveSubscription = () => plan !== null && isSubscribedStatus(status);

    const isPendingCheckout = () => isPendingStatus(status);

    const pendingPlan = () =>
        subscriptionDetails?.pendingSubscriptionPlan || subscriptionDetails?.plan || null;

    const isOnTrial = () => status === 'trial';

    const isTrialEligible = () =>
        !subscriptionDetails?.hasUsedTrial &&
        !hasActiveSubscription() &&
        !isPendingCheckout();

    const trialEndsAt = isOnTrial() ? subscriptionDetails?.trialEndsAt || subscriptionDetails?.endDate : null;

    const value = {
        plan,
        status,
        subscriptionDetails,
        price: getPrice(),
        isLoading,
        isSilver,
        isGold,
        isPlatinum,
        hasAnalytics,
        hasWebSocket,
        hasQROrdering,
        hasMediaImages,
        hasExpenseReceipts,
        hasCustomDishCategories,
        hasActiveSubscription,
        isPendingCheckout,
        pendingPlan,
        isOnTrial,
        isTrialEligible,
        trialEndsAt,
        updatePlan,
        refreshSubscription: fetchSubscription,
        PLANS,
        TRIAL_DAYS,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
